import { useEffect, useState } from "react";
import CheckboxTree from "react-checkbox-tree";
import "./style.scss";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

interface TreeNode {
  value: string | number;
  label: string;
  description?: string;
  children?: TreeNode[];
}

interface TreeCompProps {
  data: TreeNode[];
  category: string;
  className?: string;
  viewOnly?: boolean;
  checkedExternal?: string[];
  setCheckedExternal?: (values: string[]) => void;
}

const formatNodeLabel = (label: string, description?: string) => {
  return (
    <div className="flex flex-col gap-2 items-start">
      <span>{label}</span>
      {description && (
        <span style={{ fontSize: "12px", color: "#999", marginLeft: 6 }}>
          ({description})
        </span>
      )}
    </div>
  );
};

const formatTreeData = (nodes: TreeNode[]): any[] => {
  return nodes.map((node) => ({
    ...node,
    value: String(node.value),
    label: formatNodeLabel(node.label, node.description),
    children: node.children ? formatTreeData(node.children) : undefined,
  }));
};

function TreeComp({
  data,
  category,
  viewOnly = false,
  checkedExternal,
  className,
  setCheckedExternal,
}: TreeCompProps) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [internalChecked, setInternalChecked] = useState<string[]>([]);

  // Expand first-level nodes on mount
  useEffect(() => {
    const firstLevelValues = data.map((node) => node.value);
    setExpanded(firstLevelValues);
  }, [data]);

  const handleCheck = (newChecked: string[]) => {
    let updatedChecked = [...newChecked];

    // Extract moduleIds that have non-view permissions selected
    const modulesNeedingView = new Set<string>();

    updatedChecked.forEach((val) => {
      const [moduleId, permissionId] = val.split("-");
      if (permissionId && permissionId !== "1") {
        modulesNeedingView.add(moduleId);
      }
    });

    // Add missing "moduleId-1" if any non-view permission is selected
    modulesNeedingView.forEach((moduleId) => {
      const viewPermission = `${moduleId}-1`;
      if (!updatedChecked.includes(viewPermission)) {
        updatedChecked.push(viewPermission);
      }
    });

    if (setCheckedExternal) {
      setCheckedExternal(updatedChecked);
    } else {
      setInternalChecked(updatedChecked);
    }
  };

  const handleExpand = (newExpanded: string[]) => {
    setExpanded(newExpanded);
  };

  const isControlled = typeof checkedExternal !== "undefined";

  return (
    <div
      className={`tree-comp w-full ${viewOnly ? "disabled" : ""} ${className}`}
    >
      <CheckboxTree
        nodes={formatTreeData(data)}
        checked={isControlled ? checkedExternal! : internalChecked}
        expanded={expanded}
        onCheck={handleCheck}
        onExpand={handleExpand}
        disabled={viewOnly}
        icons={{
          expandOpen: <DownOutlined />,
          expandClose: <UpOutlined />,
        }}
      />
    </div>
  );
}

export default TreeComp;
